import React from 'react';
import PropTypes from 'prop-types';
import {
  FaPollH,
  FaStar,
  FaCheckCircle,
  FaComments,
  FaSmile,
  FaMeh,
  FaFrown,
  FaBuilding,
  FaSitemap,
  FaChevronDown
} from 'react-icons/fa';


export default function SurveyInsightsSkeleton() {
  return (
    <div
      className="skeleton-wireframe w-full flex flex-col gap-5 text-slate-800"
      aria-hidden="true"
      data-testid="survey-insights-skeleton"
    >
      {/* 1. Header */}
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Survey Insights</h1>
        <p className="text-xs text-slate-500">
          Capture end-user feedback, sentiment trends, and workflow CSAT intelligence across applications.
        </p>
      </div>

      {/* 2. Key Metrics Row */}
      <div className="grid grid-cols-4 gap-4">
        {/* Metric 1: CSAT Score */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FaStar className="text-amber-400" /> Average CSAT Score
          </span>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-slate-900">4.6</span>
            <span className="text-xs font-semibold text-slate-400">/ 5.0</span>
          </div>
          <div className="flex items-center gap-1 text-amber-400 text-xs mt-0.5">
            <FaStar /><FaStar /><FaStar /><FaStar /><FaStar className="text-amber-200" />
            <span className="text-[10px] font-semibold text-emerald-600 ml-1.5">+0.3 vs last month</span>
          </div>
        </div>

        {/* Metric 2: Total Responses */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FaPollH className="text-indigo-500" /> Total Submissions
          </span>
          <span className="text-2xl font-extrabold text-slate-900">142</span>
          <span className="text-[10px] text-slate-400">Recorded across 4 active flows</span>
        </div>

        {/* Metric 3: Completion Rate */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FaCheckCircle className="text-emerald-500" /> Survey Completion
          </span>
          <span className="text-2xl font-extrabold text-emerald-600">88.4%</span>
          <span className="text-[10px] text-slate-400">Low drop-off before submission</span>
        </div>

        {/* Metric 4: Net Sentiment */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FaComments className="text-blue-500" /> Net Sentiment
          </span>
          <div className="flex items-center gap-2 mt-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-xs font-bold">
              <FaSmile /> 78% Positive
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-xs font-bold">
              <FaMeh /> 15%
            </span>
          </div>
        </div>
      </div>

      {/* 3. Filter / Survey Selector Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-4 shadow-sm text-xs">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FaBuilding className="text-slate-400" /> Organization
          </span>
          <div className="flex items-center justify-between gap-3 bg-white border border-slate-300 rounded-md px-3 py-1.5 min-w-[140px] text-slate-800 font-medium">
            <span>System</span>
            <FaChevronDown className="text-[9px] text-slate-400" />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FaSitemap className="text-slate-400" /> Site Collection
          </span>
          <div className="flex items-center justify-between gap-3 bg-white border border-slate-300 rounded-md px-3 py-1.5 min-w-[170px] text-slate-800 font-medium">
            <span>Client Demonstration</span>
            <FaChevronDown className="text-[9px] text-slate-400" />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Active Survey Form</span>
          <div className="flex items-center justify-between gap-3 bg-white border border-slate-300 rounded-md px-3 py-1.5 min-w-[260px] text-slate-800 font-medium">
            <span>Q3 User Experience &amp; Onboarding Feedback</span>
            <FaChevronDown className="text-[9px] text-slate-400" />
          </div>
        </div>
      </div>

      {/* 4. Survey Form Details Card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col gap-6 shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div>
            <h2 className="text-base font-bold text-slate-900">Survey Form Structure &amp; Question Review</h2>
            <p className="text-xs text-slate-500">Live prompt templates configured for end-user runtime presentation.</p>
          </div>
          <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200">
            Form Status: Active
          </span>
        </div>

        {/* Question 1: Rating Scale */}
        <div className="flex flex-col gap-2 p-4 bg-slate-50/70 border border-slate-200 rounded-xl">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-indigo-900">Question 1 (CSAT Rating)</span>
            <span className="text-[10px] text-slate-400 font-medium">Required</span>
          </div>
          <p className="text-sm font-semibold text-slate-800">
            How satisfied are you with the step-by-step guidance provided in this workflow?
          </p>
          <div className="flex items-center gap-2 mt-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <div
                key={star}
                className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold ${
                  star === 5
                    ? 'bg-amber-500 text-white border-amber-600 shadow-xs'
                    : 'bg-white text-slate-600 border-slate-200'
                }`}
              >
                <FaStar className={star === 5 ? 'text-white' : 'text-amber-400'} />
                <span>{star}</span>
              </div>
            ))}
            <span className="text-[11px] text-slate-500 ml-2 font-medium">(5 = Extremely Satisfied)</span>
          </div>
        </div>

        {/* Question 2: Single/Multiple Choice Friction Point */}
        <div className="flex flex-col gap-2 p-4 bg-slate-50/70 border border-slate-200 rounded-xl">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-indigo-900">Question 2 (Friction Diagnostic)</span>
            <span className="text-[10px] text-slate-400 font-medium">Optional</span>
          </div>
          <p className="text-sm font-semibold text-slate-800">
            Did you encounter any confusion or bottlenecks while completing the flow?
          </p>
          <div className="flex flex-wrap gap-2 mt-1">
            <span className="px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-semibold">
              None — smooth experience
            </span>
            <span className="px-3 py-1.5 rounded-lg bg-white text-slate-600 border border-slate-200 text-xs font-medium">
              Step 3 instructions were unclear
            </span>
            <span className="px-3 py-1.5 rounded-lg bg-white text-slate-600 border border-slate-200 text-xs font-medium">
              Page took too long to respond
            </span>
            <span className="px-3 py-1.5 rounded-lg bg-white text-slate-600 border border-slate-200 text-xs font-medium">
              Button target was hard to find
            </span>
          </div>
        </div>

        {/* Question 3: Qualitative Feedback */}
        <div className="flex flex-col gap-2 p-4 bg-slate-50/70 border border-slate-200 rounded-xl">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-indigo-900">Question 3 (Open Feedback)</span>
            <span className="text-[10px] text-slate-400 font-medium">Optional</span>
          </div>
          <p className="text-sm font-semibold text-slate-800">
            What additional tools or instructions would help you complete this task faster?
          </p>
          <div className="w-full bg-white border border-slate-200 rounded-lg p-3 text-xs text-slate-400 min-h-[50px]">
            End-user freeform comments and feature requests appear here...
          </div>
        </div>
      </div>
    </div>
  );
}

SurveyInsightsSkeleton.propTypes = {
  featureName: PropTypes.string
};
